/**
 * Group Layer Renderer
 * Renders group layers with their children
 */

import React from 'react';
import type { GroupLayer, Layer } from '@/types/layers';
import type { ResolvedPosition, ContainerDimensions } from '@/lib/position/resolver';

interface GroupLayerRendererProps {
  layer: GroupLayer;
  position: ResolvedPosition;
  containerDimensions: ContainerDimensions;
  variableValues: Record<string, string>;
  selectedLayerIds: string[];
  hoveredLayerId: string | null;
  renderLayer: (layer: Layer) => React.ReactNode;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function GroupLayerRenderer({
  layer,
  position,
  containerDimensions,
  renderLayer,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: GroupLayerRendererProps) {
  const { transform, opacity, width, height, layout, children } = layer;

  const resolvedWidth = position.width ?? (typeof width === 'number' ? width : containerDimensions.width);
  const resolvedHeight = position.height ?? (typeof height === 'number' ? height : containerDimensions.height);

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: resolvedWidth,
    height: resolvedHeight,
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #60A5FA' : 'none',
    outlineOffset: 4,
    cursor: 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
  };

  // Apply layout if specified
  if (layout) {
    const padding = typeof layout.padding === 'number'
      ? layout.padding
      : layout.padding
        ? `${layout.padding.top ?? 0}px ${layout.padding.right ?? 0}px ${layout.padding.bottom ?? 0}px ${layout.padding.left ?? 0}px`
        : 0;

    if (layout.type === 'stack') {
      Object.assign(containerStyle, {
        display: 'flex',
        flexDirection: 'column',
        gap: layout.gap ?? 0,
        alignItems: layout.align === 'start' ? 'flex-start'
          : layout.align === 'end' ? 'flex-end'
          : layout.align === 'stretch' ? 'stretch'
          : 'center',
        justifyContent: layout.justify === 'start' ? 'flex-start'
          : layout.justify === 'end' ? 'flex-end'
          : layout.justify === 'space-between' ? 'space-between'
          : layout.justify === 'space-around' ? 'space-around'
          : 'center',
        padding,
      });
    } else if (layout.type === 'row') {
      Object.assign(containerStyle, {
        display: 'flex',
        flexDirection: 'row',
        gap: layout.gap ?? 0,
        alignItems: layout.align === 'start' ? 'flex-start'
          : layout.align === 'end' ? 'flex-end'
          : layout.align === 'stretch' ? 'stretch'
          : 'center',
        justifyContent: layout.justify === 'start' ? 'flex-start'
          : layout.justify === 'end' ? 'flex-end'
          : layout.justify === 'space-between' ? 'space-between'
          : layout.justify === 'space-around' ? 'space-around'
          : 'center',
        padding,
      });
    }
    // 'free' layout keeps absolute positioning
  }

  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
      data-layer-type="group"
    >
      {children.filter(child => child.visible).map(renderLayer)}
    </div>
  );
}
